import { useCallback, useEffect, useMemo, useState } from 'react'

import type { LoginThrottleFailure } from '@/features/actions/login'

/**
 * What the banners should render.
 *
 * A discriminated union rather than a flat bag of optional fields, so every
 * phase carries exactly the data it draws — and no phase can be rendered with
 * values that belong to another one.
 */
export type ThrottleFeedback =
  | { phase: 'none' }
  | { phase: 'error' }
  | { phase: 'warning'; attemptsRemaining: number }
  | { phase: 'locked-live'; remainingMs: number }
  | { phase: 'locked-static'; unlockAtDisplay: string }

export type ThrottlePhase = ThrottleFeedback['phase']

export type LoginThrottle = ThrottleFeedback & {
  registerFailure: (failure: LoginThrottleFailure) => void
  registerSuccess: () => void
}

/**
 * Holds the failed-login feedback state for the login form, including the
 * countdown clock that runs while the account is locked.
 *
 * The phase is derived from what the server reports, never counted on the
 * client — throttling is backend-owned, and anything counted here would be
 * bypassable. The one thing computed locally is how much of the lock is left,
 * which is just `lockedUntilEpochMs` measured against the current time.
 */
export function useLoginThrottle(): LoginThrottle {
  const [failure, setFailure] = useState<LoginThrottleFailure | null>(null)
  const [now, setNow] = useState(() => Date.now())

  const feedback = useMemo(() => deriveFeedback(failure, now), [failure, now])

  // Tick once a second while a lock is on screen. Both lock phases are derived
  // against `now`, so the moment the lock runs out the phase changes, this
  // effect re-runs and the interval is cleared — the clock stops itself and the
  // form re-enables without a refresh.
  const ticking = feedback.phase === 'locked-live' || feedback.phase === 'locked-static'
  useEffect(() => {
    if (!ticking) return

    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [ticking])

  const registerFailure = useCallback((next: LoginThrottleFailure) => {
    // re-read the clock so a lock is measured from the response, not from
    // whenever this component last rendered
    setNow(Date.now())
    setFailure(next)
  }, [])

  const registerSuccess = useCallback(() => {
    setFailure(null)
  }, [])

  return useMemo(
    () => ({ ...feedback, registerFailure, registerSuccess }),
    [feedback, registerFailure, registerSuccess],
  )
}

function deriveFeedback(failure: LoginThrottleFailure | null, now: number): ThrottleFeedback {
  if (!failure) return { phase: 'none' }

  switch (failure.errorCode) {
    case 111: {
      // the lock has run out: the form becomes usable again, but the failure
      // that caused it still stands, so the plain error banner stays up
      if (failure.lockedUntilEpochMs - now <= 0) return { phase: 'error' }

      // the server pre-formatted an unlock time, so show that instead of a
      // ticking clock — never reformat it, it is already in the account's
      // own time format
      if (failure.unlockAtDisplay !== undefined) {
        return { phase: 'locked-static', unlockAtDisplay: failure.unlockAtDisplay }
      }

      return { phase: 'locked-live', remainingMs: failure.lockedUntilEpochMs - now }
    }

    case 112:
      return { phase: 'warning', attemptsRemaining: failure.attemptsRemaining }

    // the 1st/2nd failure, and any code the UI does not model yet
    default:
      return { phase: 'error' }
  }
}
